import { useState, useEffect } from "react";
import { FaSync } from "react-icons/fa";

const sourceItems = [
  { id: 1, name: "Q2 Campaign Brief", status: "Working on it", color: "#fdab3d" },
  { id: 2, name: "Website Redesign", status: "Done", color: "#00c875" },
  { id: 3, name: "Influencer Outreach", status: "Stuck", color: "#e2445c" },
];

const destItems = [
  { id: 1, name: "Q2 Campaign Brief", status: "Working on it", color: "#fdab3d" },
  { id: 2, name: "Website Redesign", status: "Done", color: "#00c875" },
];

type MiniBoardRow = {
  id: number;
  name: string;
  status: string;
  color: string;
};

function BoardMiniCard({
  name,
  status,
  color,
  visible,
}: {
  name: string;
  status: string;
  color: string;
  visible: boolean;
}) {
  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded border border-solid border-[#e6e9ef] bg-white transition-all duration-500"
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(6px)" }}
    >
      <div className="w-[3px] h-5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
      <span className="text-[11px] text-[#323338] flex-1 truncate">{name}</span>
      <span
        className="text-[10px] px-1.5 py-0.5 rounded-full text-white flex-shrink-0"
        style={{ backgroundColor: color }}
      >
        {status}
      </span>
    </div>
  );
}

function SyncMiniBoard({
  title,
  headerDotClassName,
  items,
  getItemVisible,
  isWaitingVisible = false,
  className = "",
}: {
  title: string;
  headerDotClassName: string;
  items: MiniBoardRow[];
  getItemVisible: (index: number) => boolean;
  isWaitingVisible?: boolean;
  className?: string;
}) {
  return (
    <div
      className={[
        "flex-1 bg-[#f6f7fb] rounded-xl border border-solid border-[#d0d4e4] overflow-hidden",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="flex items-center gap-2 px-3 py-2 bg-[#eceff8] border-b border-[#d0d4e4]">
        <div className={headerDotClassName} />
        <span className="text-[11px] font-semibold text-[#323338]">{title}</span>
      </div>
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-solid border-x-0 border-t-0 border-[#e6e9ef] bg-white">
        <span className="text-[10px] text-[#676879] flex-1">Item</span>
        <span className="text-[10px] text-[#676879] w-20 text-center">Status</span>
      </div>
      <div className="flex flex-col gap-1 p-2">
        {items.map((item, i) => (
          <BoardMiniCard
            key={item.id}
            name={item.name}
            status={item.status}
            color={item.color}
            visible={getItemVisible(i)}
          />
        ))}
        {isWaitingVisible && (
          <div className="flex justify-center items-center py-3">
            <span className="text-[10px] text-[#676879] italic">Waiting for sync…</span>
          </div>
        )}
      </div>
    </div>
  );
}

function SyncConnector({ syncStep }: { syncStep: number }) {
  const isActive = syncStep > 0;
  const statusLabel =
    syncStep === 0 ? "SYNC" : syncStep === 1 || syncStep === 2 ? "SYNCING…" : "SYNCED ✓";

  return (
    <div className="flex flex-col items-center gap-2 flex-shrink-0 w-[100px]">
      <div className="flex relative justify-center items-center w-full">
        <div className="absolute h-[2px] w-full bg-[#e6e9ef] rounded-full overflow-hidden">
          <div
            className="absolute h-full bg-gradient-to-r from-[#0073ea] to-[#5c9bff] rounded-full transition-all duration-700"
            style={{
              width: isActive ? "100%" : "0%",
              left: 0,
            }}
          />
        </div>
        <div
          className="flex relative z-10 justify-center items-center w-9 h-9 rounded-full border-2 border-solid transition-all duration-300"
          style={{
            backgroundColor: isActive ? "#0073ea" : "#fff",
            borderColor: isActive ? "#0073ea" : "#d0d4e4",
            boxShadow: isActive ? "0 0 0 4px rgba(0,115,234,0.12)" : "none",
          }}
        >
          <FaSync size={14} color={isActive ? "#fff" : "#676879"} />
        </div>
      </div>
      <span
        className="text-[10px] font-semibold tracking-wide transition-colors duration-300"
        style={{ color: isActive ? "#0073ea" : "#676879" }}
      >
        {statusLabel}
      </span>
    </div>
  );
}

export function SyncVisualHook() {
  const [syncStep, setSyncStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeout(() => {
        setSyncStep((s) => (s < 3 ? s + 1 : 0));
      }, 600);
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center gap-4 w-full max-w-[680px] mx-auto select-none mb-4">
      <SyncMiniBoard
        title="Marketing Board"
        headerDotClassName="w-3 h-3 rounded-full bg-[#0073ea]"
        items={sourceItems}
        getItemVisible={() => true}
      />

      <SyncConnector syncStep={syncStep} />

      <SyncMiniBoard
        title="Sales Pipeline"
        headerDotClassName="w-3 h-3 rounded-full bg-[#9c58f3]"
        items={destItems}
        getItemVisible={(i) => syncStep >= i + 2}
        isWaitingVisible={syncStep < 2}
        className="min-h-[194.5px]"
      />
    </div>
  );
}
